import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  useWindowDimensions,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { Text, useTheme } from 'react-native-paper';
import { Input, Button as DSButton } from '@coffeeplexity/ui';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Coffee, Eye, EyeOff, AlertCircle } from 'lucide-react-native';
import { loginSchema, LoginFormData } from '@coffeeplexity/shared';
import { useLogin } from '@coffeeplexity/api-client';
import { useAuthContext } from '@coffeeplexity/shared';
import Toast from 'react-native-toast-message';
// 🚨 REMOVE BEFORE LAUNCH - Start
import {
  shouldAutoSignIn,
  getAutoSignInCredentials,
  logAutoSignInActivity,
} from '../../services/autoSignInService';
// 🚨 REMOVE BEFORE LAUNCH - End

// Utility functions for responsive design
const isTablet = (width: number): boolean => width >= 768;
const isLandscape = (width: number, height: number): boolean => width > height;

interface ResponsiveStyles {
  scrollContent: ViewStyle;
  header: ViewStyle;
  iconSize: number;
  title: TextStyle;
  subtitle: TextStyle;
  form: ViewStyle;
  inputContainer: ViewStyle;
  forgotPasswordButton: ViewStyle;
  loginButton: ViewStyle;
  divider: ViewStyle;
  dividerText: TextStyle;
  footer: ViewStyle;
  footerText: TextStyle;
}

export function LoginScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const { login: contextLogin } = useAuthContext();
  const loginMutation = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  // 🚨 REMOVE BEFORE LAUNCH - Start
  const [autoSignInAttempted, setAutoSignInAttempted] = useState(false);
  // 🚨 REMOVE BEFORE LAUNCH - End
  const windowDimensions = useWindowDimensions();

  // Responsive layout detection
  const isTabletDevice = isTablet(windowDimensions.width);
  const isLandscapeMode = isLandscape(
    windowDimensions.width,
    windowDimensions.height
  );
  const screenWidth = windowDimensions.width;
  const isSmallScreen = screenWidth < 375;
  const isLargeScreen = screenWidth > 768;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // ========================================================================
  // 🚨🚨🚨 REMOVE BEFORE LAUNCH - Auto-sign-in effect (TEMPORARY) 🚨🚨🚨
  // ========================================================================
  useEffect(() => {
    // Only attempt auto-sign-in once when component mounts
    if (autoSignInAttempted) return;

    const shouldAutoLogin = shouldAutoSignIn();

    if (shouldAutoLogin) {
      const credentials = getAutoSignInCredentials();

      if (credentials) {
        logAutoSignInActivity('Auto-sign-in triggered', {
          email: credentials.email,
        });

        // Set form values
        setValue('email', credentials.email);
        setValue('password', credentials.password);

        // Automatically submit the form after a short delay
        // to ensure form is fully initialized
        const timer = setTimeout(() => {
          handleSubmit(onSubmit)();
        }, 500);

        setAutoSignInAttempted(true);

        return () => clearTimeout(timer);
      }
    }
  }, []); // Empty dependency array - only run once on mount
  // ========================================================================
  // 🚨🚨🚨 END REMOVE BEFORE LAUNCH 🚨🚨🚨
  // ========================================================================

  const onSubmit = async (data: LoginFormData) => {
    try {
      setErrorMessage(''); // Clear any previous errors
      const response = await loginMutation.mutateAsync(data);
      await contextLogin(response.user, response.tokens);
      Toast.show({
        type: 'success',
        text1: 'Welcome back!',
        text2: 'Successfully logged in.',
      });
      // Navigation will be handled by the auth context
    } catch (error: any) {
      let message = 'Please check your credentials and try again.';

      if (error.status === 401) {
        message = 'Invalid email or password';
      } else if (error.status === 422) {
        message = 'Please check your input and try again';
      } else if (error.message) {
        message = error.message;
      }

      setErrorMessage(message);
      Toast.show({
        type: 'error',
        text1: 'Login Failed',
        text2: message,
      });
      setError('root', { message });
    }
  };

  // Responsive styles
  const responsiveStyles = getResponsiveStyles(
    isTabletDevice,
    isLandscapeMode,
    isSmallScreen,
    isLargeScreen
  );

  return (
    <SafeAreaView
      style={{
        ...styles.container,
        backgroundColor: theme.colors.background,
      }}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingView}
      >
        <ScrollView
          contentContainerStyle={{
            ...styles.scrollContent,
            ...responsiveStyles.scrollContent,
          }}
          showsVerticalScrollIndicator={false}
        >
          {/* ============================================================ */}
          {/* 🚨🚨🚨 REMOVE BEFORE LAUNCH - Demo Mode Banner 🚨🚨🚨 */}
          {/* ============================================================ */}
          {/* Demo Mode Banner */}
          {shouldAutoSignIn() && (
            <View
              style={{
                ...styles.demoBanner,
                backgroundColor: theme.colors.primaryContainer,
              }}
            >
              <AlertCircle size={16} color={theme.colors.onPrimaryContainer} />
              <Text
                variant='bodySmall'
                style={{
                  ...styles.demoBannerText,
                  color: theme.colors.onPrimaryContainer,
                }}
              >
                Demo Mode: Auto-sign-in enabled
              </Text>
            </View>
          )}
          {/* ============================================================ */}
          {/* 🚨🚨🚨 END REMOVE BEFORE LAUNCH 🚨🚨🚨 */}
          {/* ============================================================ */}

          {/* Header */}
          <View style={{ ...styles.header, ...responsiveStyles.header }}>
            <Coffee
              size={responsiveStyles.iconSize}
              color={theme.colors.primary}
            />
            <Text
              variant={isLargeScreen ? 'displaySmall' : 'headlineLarge'}
              style={{
                ...styles.title,
                ...responsiveStyles.title,
                color: theme.colors.onBackground,
              }}
            >
              Welcome Back
            </Text>
            <Text
              variant={isSmallScreen ? 'bodyMedium' : 'bodyLarge'}
              style={{
                ...styles.subtitle,
                ...responsiveStyles.subtitle,
                color: theme.colors.onSurfaceVariant,
              }}
            >
              Sign in to continue your coffee journey
            </Text>
          </View>

          {/* Form */}
          <View style={{ ...styles.form, ...responsiveStyles.form }}>
            {/* Error Message - Minimalist Design */}
            {errorMessage ? (
              <View style={styles.errorContainer}>
                <View style={styles.errorContent}>
                  <AlertCircle
                    size={16}
                    color={theme.colors.error}
                    strokeWidth={2}
                  />
                  <Text
                    style={{
                      ...styles.errorText,
                      color: theme.colors.error,
                    }}
                  >
                    {errorMessage}
                  </Text>
                </View>
              </View>
            ) : null}

            <Controller
              control={control}
              name='email'
              render={({ field: { onChange, onBlur, value } }) => (
                <View
                  style={{
                    ...styles.inputContainer,
                    ...responsiveStyles.inputContainer,
                  }}
                >
                  <Input
                    label='Email'
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    keyboardType='email-address'
                    autoCapitalize='none'
                    autoComplete='email'
                    error={errors.email?.message}
                  />
                </View>
              )}
            />

            <Controller
              control={control}
              name='password'
              render={({ field: { onChange, onBlur, value } }) => (
                <View
                  style={{
                    ...styles.inputContainer,
                    ...responsiveStyles.inputContainer,
                  }}
                >
                  <Input
                    label='Password'
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    secureTextEntry={!showPassword}
                    autoComplete='password'
                    error={errors.password?.message}
                    rightIcon={
                      <Pressable onPress={() => setShowPassword(!showPassword)}>
                        {showPassword ? (
                          <EyeOff
                            size={20}
                            color={theme.colors.onSurfaceVariant}
                          />
                        ) : (
                          <Eye
                            size={20}
                            color={theme.colors.onSurfaceVariant}
                          />
                        )}
                      </Pressable>
                    }
                  />
                </View>
              )}
            />

            {/* Forgot Password Link */}
            <DSButton
              variant='ghost'
              onPress={() => navigation.navigate('forgotPassword' as never)}
              style={{
                ...styles.forgotPasswordButton,
                ...responsiveStyles.forgotPasswordButton,
              }}
            >
              Forgot Password?
            </DSButton>

            {/* Login Button */}
            <DSButton
              variant='primary'
              onPress={handleSubmit(onSubmit)}
              loading={loginMutation.isPending}
              disabled={loginMutation.isPending}
              style={{
                ...styles.loginButton,
                ...responsiveStyles.loginButton,
              }}
            >
              Sign In
            </DSButton>

            {/* Divider */}
            <View style={{ ...styles.divider, ...responsiveStyles.divider }}>
              <View
                style={{
                  ...styles.dividerLine,
                  backgroundColor: theme.colors.outline,
                }}
              />
              <View
                style={{
                  ...styles.dividerLine,
                  backgroundColor: theme.colors.outline,
                }}
              />
            </View>
          </View>

          {/* Sign Up Link */}
          <View style={{ ...styles.footer, ...responsiveStyles.footer }}>
            <Text
              style={{
                ...styles.footerText,
                ...responsiveStyles.footerText,
                color: theme.colors.onSurfaceVariant,
              }}
            >
              Don't have an account?{' '}
            </Text>
            <DSButton
              variant='ghost'
              onPress={() => navigation.navigate('register' as never)}
              style={styles.signUpButton}
            >
              Sign Up
            </DSButton>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

/**
 * Generates responsive styles based on device characteristics
 * Ensures consistent layout across mobile, tablet, and web platforms
 */
function getResponsiveStyles(
  isTablet: boolean,
  isLandscape: boolean,
  isSmallScreen: boolean,
  isLargeScreen: boolean
): ResponsiveStyles {
  const baseIconSize = 64;
  const tabletIconSize = 80;
  const landscapeIconSize = 56;

  return {
    scrollContent: {
      paddingHorizontal: isSmallScreen ? 16 : isLargeScreen ? 48 : 24,
      paddingVertical: isLandscape ? 12 : 24,
    },
    header: {
      marginTop: isLandscape ? 16 : 40,
      marginBottom: isLandscape ? 8 : 12,
    },
    iconSize: isTablet
      ? tabletIconSize
      : isLandscape
        ? landscapeIconSize
        : baseIconSize,
    title: {
      marginTop: isSmallScreen ? 12 : 16,
      fontSize: isLargeScreen ? 32 : isSmallScreen ? 24 : 28,
    },
    subtitle: {
      marginTop: isSmallScreen ? 4 : 8,
    },
    form: {
      flex: 1,
      maxWidth: isLargeScreen ? 500 : undefined,
      alignSelf: isLargeScreen ? 'center' : 'stretch',
      width: isLargeScreen ? '100%' : undefined,
      maxHeight: 'fit-content',
    },
    inputContainer: {
      marginBottom: isSmallScreen ? 4 : 6,
    },
    forgotPasswordButton: {
      alignSelf: 'flex-end',
      marginBottom: isSmallScreen ? 16 : 24,
      marginTop: 16,
    },
    loginButton: {
      marginBottom: isSmallScreen ? 16 : 24,
    },
    divider: {
      marginVertical: isSmallScreen ? 16 : 24,
    },
    dividerText: {
      fontSize: isSmallScreen ? 12 : 14,
      marginHorizontal: isSmallScreen ? 12 : 16,
    },
    footer: {
      flexDirection: isSmallScreen ? 'column' : 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: isSmallScreen ? 4 : 0,
    },
    footerText: {
      fontSize: isSmallScreen ? 14 : 16,
    },
  };
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  header: {
    alignItems: 'center',
  },
  title: {
    fontWeight: 'bold',
  },
  subtitle: {
    textAlign: 'center',
  },
  form: {
    flex: 1,
  },
  inputContainer: {},
  forgotPasswordButton: {
    alignSelf: 'flex-end',
  },
  loginButton: {},
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  dividerText: {
    marginHorizontal: 16,
    fontSize: 14,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 16,
  },
  signUpButton: {
    marginLeft: 4,
  },
  errorContainer: {
    marginBottom: 20,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 59, 48, 0.08)', // Subtle red tint
    borderWidth: 1,
    borderColor: 'rgba(255, 59, 48, 0.2)', // Minimal border
  },
  errorContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '500',
    letterSpacing: -0.2,
  },
  // 🚨 REMOVE BEFORE LAUNCH - Start (demo banner styles)
  demoBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 16,
    gap: 8,
  },
  demoBannerText: {
    fontWeight: '600',
  },
  // 🚨 REMOVE BEFORE LAUNCH - End
});
